import React, { useState, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  List,
  ListItem,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

export interface CandidateComment {
  id: string;
  comment: string;
  commentedBy?: string;
  date?: string;
}

interface CommentsSectionProps {
  comments: CandidateComment[];
  handleAddComment: (commentText: string) => void;
  handleEditComment: (updatedComment: CandidateComment) => void;
  handleDeleteComment: (id: string) => void;
}

const CommentsSection: React.FC<CommentsSectionProps> = React.memo(
  ({ comments, handleAddComment, handleEditComment, handleDeleteComment }) => {
    const [newComment, setNewComment] = useState("");
    const [editingComment, setEditingComment] = useState<string | null>(null);
    const [editText, setEditText] = useState("");

    const onAddComment = useCallback(() => {
      handleAddComment(newComment);
      setNewComment("");
    }, [handleAddComment, newComment]);

    const startEditing = (id: string, currentText: string) => {
      setEditingComment(id);
      setEditText(currentText);
    };

    const saveEdit = async (id: string) => {
      if (!editText.trim()) return;
      const updatedComment: CandidateComment = {
        id,
        comment: editText,
      };
      await handleEditComment(updatedComment);
      setEditingComment(null);
    };

    const cancelEdit = () => {
      setEditingComment(null);
      setEditText("");
    };

    const onDeleteComment = async (id: string) => {
      await handleDeleteComment(id);
    };

    const formatCommentDate = (date?: string) => {
      if (!date) return "No date available";

      const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(date);
      const normalizedDate = hasTimezone ? date : `${date}Z`;
      const parsedDate = new Date(normalizedDate);

      if (Number.isNaN(parsedDate.getTime())) {
        return "Invalid date";
      }

      return new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Chicago",
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(parsedDate);
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <List
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            padding: 2,
            marginBottom: 1,
          }}
        >
          {comments.map((comment) => (
            <ListItem key={comment.id} alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                {comment.commentedBy?.trim() && (
                  <Typography variant="subtitle2">
                    {comment.commentedBy}
                  </Typography>
                )}
                {editingComment === comment.id ? (
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                ) : (
                  <Typography variant="body2">{comment.comment}</Typography>
                )}
                <Typography variant="caption" color="textSecondary">
                  {formatCommentDate(comment.date)}
                </Typography>
              </Box>
              <Box>
                {editingComment === comment.id ? (
                  <>
                    <Button size="small" onClick={() => saveEdit(comment.id)}>
                      Save
                    </Button>
                    <Button size="small" onClick={cancelEdit}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => startEditing(comment.id, comment.comment)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onDeleteComment(comment.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </ListItem>
          ))}
        </List>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: 1,
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Add a comment"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={onAddComment}
            sx={{ marginLeft: 1 }}
          >
            Send
          </Button>
        </Box>
      </Box>
    );
  },
);

export default CommentsSection;
